import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Rocket, X, ArrowRight, PartyPopper } from "lucide-react";

export const SetupChecklist = () => {
  const { t } = useTranslation("app");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const hideKey = `il_setup_hidden_${user?.id || ""}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(hideKey) === "1");

  useEffect(() => {
    if (user?.role !== "admin" || hidden) return;
    api.get("/onboarding/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user?.role, hidden]);

  if (user?.role !== "admin" || hidden || !status) return null;

  const steps = [
    { key: "azienda", done: true, label: t("setup.stepAzienda") },
    { key: "admin", done: true, label: t("setup.stepAdmin") },
    { key: "dipendente", done: status.employees > 0, label: t("setup.stepDipendente"), cta: t("setup.ctaDipendente"), to: "/anagrafica/dipendenti" },
    { key: "commessa", done: status.commesse > 0, label: t("setup.stepCommessa"), cta: t("setup.ctaCommessa"), to: "/anagrafica/commesse" },
    { key: "rapportino", done: status.rapportini > 0, label: t("setup.stepRapportino"), cta: t("setup.ctaRapportino"), to: "/rapportino/new" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  // Account avviato da tempo: non mostrare nulla
  if (allDone && status.rapportini > 2) return null;

  const nextStep = steps.find((s) => !s.done);
  const dismiss = () => { localStorage.setItem(hideKey, "1"); setHidden(true); };

  return (
    <div
      className="rounded-2xl border-2 border-[#0044FF]/20 bg-gradient-to-br from-blue-50 to-white p-5 md:p-6"
      data-testid="setup-checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {allDone
            ? <PartyPopper className="h-6 w-6 text-emerald-600" />
            : <Rocket className="h-6 w-6 text-[#0044FF]" />}
          <div>
            <div className="font-heading font-extrabold text-lg text-slate-900">
              {allDone ? t("setup.titleDone") : t("setup.title")}
            </div>
            <div className="text-sm text-slate-600">
              {allDone ? t("setup.subDone") : t("setup.sub", { done: doneCount, total: steps.length })}
            </div>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 p-1"
          data-testid="setup-dismiss-btn"
          aria-label="close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#0044FF] transition-all duration-500"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      <ul className="mt-4 space-y-2">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-2.5" data-testid={`setup-step-${s.key}`}>
            {s.done
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              : <Circle className={`h-5 w-5 shrink-0 ${s === nextStep ? "text-[#0044FF]" : "text-slate-300"}`} />}
            <span className={`text-sm ${s.done ? "text-slate-500 line-through" : s === nextStep ? "text-slate-900 font-bold" : "text-slate-600"}`}>
              {s.label}
            </span>
            {!s.done && s === nextStep && s.to && (
              <Button
                size="sm"
                className="ml-auto bg-[#0044FF] hover:bg-blue-700 text-white"
                onClick={() => navigate(s.to)}
                data-testid={`setup-cta-${s.key}`}
              >
                {s.cta} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {allDone && (
        <Button
          size="sm"
          className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={dismiss}
          data-testid="setup-finish-btn"
        >
          {t("setup.finishBtn")}
        </Button>
      )}
    </div>
  );
};
